import { TableView } from 'api/models/TableView';
import {
  FilterTypeSelection,
  MODEL_FILTER_TYPES,
  ModelFilterType,
} from 'dashboard409a/components/ModelsTable/ModelsTable.types';
import { modelsColumns } from 'dashboard409a/pages/Workboard/common/colConfig';
import { DEFAULT_VIEW_ID, MODELS_COLUMN_IDS } from 'dashboard409a/pages/Workboard/common/constants';
import { DEFAULT_TRAILING_MONTHS, getTrailingDateRange, parseTrailingMonthsFilter, type TrailingMonthsOption } from 'dashboard409a/pages/Workboard/common/trailingDates';
import { UserGroup } from 'dashboard409a/pages/Workboard/UserGroups/UserGroups.types';
import {
  normalizeFilterTypeSelection,
  normalizeModelFilterType,
} from 'dashboard409a/utilities/modelFilterNormalization';
import { NormalizableDate, normalizeDate } from 'utilities/dateUtils';
import { ModelView, ViewUserId, WORKBOARD_META_COLUMNS, WorkboardTableDefinition } from '../types/workboardTableViews/types';

const DEFAULT_COLUMN_ORDER = modelsColumns.map(col => col.id);

const EMPTY_TABLE_DEFINITION: WorkboardTableDefinition = {
  columns: {},
  sortKeys: [],
  groupBy: 'companies',
  filters: [],
  filterExclusions: [],
};

export const normalizeTableDefinition = (
  definition?: WorkboardTableDefinition | Record<string, unknown> | null
): WorkboardTableDefinition => {
  if (!definition || typeof definition !== 'object') {
    return { ...EMPTY_TABLE_DEFINITION };
  }

  const def = definition as Record<string, unknown>;
  return {
    columns: (def.columns as Record<number, string>) ?? {},
    sortKeys: (def.sortKeys as WorkboardTableDefinition['sortKeys']) ?? [],
    groupBy: (def.groupBy as WorkboardTableDefinition['groupBy']) ?? 'companies',
    filters: (def.filters as WorkboardTableDefinition['filters']) ?? [],
    filterExclusions: (def.filterExclusions as string[]) ?? [],
    columnWidths: (def.columnWidths as WorkboardTableDefinition['columnWidths']) ?? undefined,
  };
};

const encodeUserRef = (ref: ViewUserId): string =>
  ref.groupId ? `group:${ref.groupId}:${ref.userId}` : `user:${ref.userId}`;

const decodeUserRef = (value: string | number): ViewUserId | null => {
  if (typeof value === 'number') {
    return { userId: value, groupId: null };
  }

  if (value.startsWith('group:')) {
    const parts = value.split(':');
    const groupId = parts[1];
    const userId = Number(parts[2]);
    if (groupId && !Number.isNaN(userId)) {
      return { userId, groupId };
    }
    return null;
  }

  if (value.startsWith('user:')) {
    const userId = Number(value.slice(5));
    if (!Number.isNaN(userId)) {
      return { userId, groupId: null };
    }
    return null;
  }

  const numeric = Number(value);
  if (!Number.isNaN(numeric)) {
    return { userId: numeric, groupId: null };
  }

  return null;
};

const toIsoDateString = (date: Date | null): string | null => {
  if (!date || Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toISOString().split('T')[0];
};

const filterTypeSelectionToFilterType = (selection: FilterTypeSelection): ModelFilterType => {
  if (selection.all || (selection.assigned && selection.unassigned)) {
    return MODEL_FILTER_TYPES.ALL;
  }
  if (selection.unassigned && !selection.assigned) {
    return MODEL_FILTER_TYPES.UNASSIGNED;
  }
  return MODEL_FILTER_TYPES.ASSIGNED;
};

const decodeAssignmentFilters = (
  filters: WorkboardTableDefinition['filters']
): FilterTypeSelection => {
  const assignmentValues = filters
    .filter(([col]) => col === WORKBOARD_META_COLUMNS.ASSIGNMENT)
    .map(([, [, value]]) => String(value));

  if (assignmentValues.length === 0) {
    return { assigned: true, unassigned: false, all: false };
  }

  return {
    assigned: assignmentValues.includes('assigned'),
    unassigned: assignmentValues.includes('unassigned'),
    all: assignmentValues.includes('all'),
  };
};

const buildColumnVisibility = (
  definition: WorkboardTableDefinition
): Record<string, boolean> => {
  const visibleColumnIds = new Set(Object.values(definition.columns));
  const hiddenColumnIds = new Set(definition.filterExclusions ?? []);
  const columnVisibility: Record<string, boolean> = {};

  DEFAULT_COLUMN_ORDER.forEach(columnId => {
    if (columnId === MODELS_COLUMN_IDS.COMPANY_NAME) {
      return;
    }
    if (hiddenColumnIds.has(columnId)) {
      columnVisibility[columnId] = false;
      return;
    }
    if (visibleColumnIds.size > 0) {
      columnVisibility[columnId] = visibleColumnIds.has(columnId);
    }
  });

  return columnVisibility;
};

const buildColumnsFromVisibility = (
  columnVisibility: Record<string, boolean> = {}
): Pick<WorkboardTableDefinition, 'columns' | 'filterExclusions'> => {
  const visibleColumns = DEFAULT_COLUMN_ORDER.filter(columnId => {
    if (columnId === MODELS_COLUMN_IDS.COMPANY_NAME) {
      return true;
    }
    return columnVisibility[columnId] !== false;
  });

  const columns = visibleColumns.reduce<Record<number, string>>((acc, columnId, index) => {
    acc[index] = columnId;
    return acc;
  }, {});

  const filterExclusions = DEFAULT_COLUMN_ORDER.filter(
    columnId =>
      columnId !== MODELS_COLUMN_IDS.COMPANY_NAME && columnVisibility[columnId] === false
  );

  return { columns, filterExclusions };
};

const decodeTrailingMonths = (
  filters: WorkboardTableDefinition['filters']
): TrailingMonthsOption | null => {
  const trailingFilter = filters.find(([col]) => col === WORKBOARD_META_COLUMNS.TRAILING_DATES);
  if (!trailingFilter) {
    return null;
  }
  return parseTrailingMonthsFilter(trailingFilter[1][1]);
};

const modelViewToTableDefinition = (view: ModelView): WorkboardTableDefinition => {
  const filterTypeSelection = normalizeFilterTypeSelection(
    view.filterTypeSelection as Record<string, unknown> | undefined,
    view.filterType
  );

  const filters: WorkboardTableDefinition['filters'] = [];

  view.preparerIds.forEach(ref => {
    filters.push([MODELS_COLUMN_IDS.MODEL_PREPARER, ['=', encodeUserRef(ref)]]);
  });

  view.reviewerIds.forEach(ref => {
    filters.push([MODELS_COLUMN_IDS.MODEL_REVIEWER, ['=', encodeUserRef(ref)]]);
  });

  let statusValues: string[] = [];
  if (Array.isArray(view.searchStatus)) {
    statusValues = view.searchStatus;
  } else if (view.searchStatus) {
    statusValues = [view.searchStatus];
  }

  statusValues.forEach(status => {
    filters.push([MODELS_COLUMN_IDS.MODEL_STATUS, ['=', status]]);
  });

  const fromDate = toIsoDateString(normalizeDate(view.fromDate as NormalizableDate));
  const toDate = toIsoDateString(normalizeDate(view.toDate as NormalizableDate));

  if (view.useTrailingDates) {
    const trailingMonths = view.trailingMonths ?? DEFAULT_TRAILING_MONTHS;
    filters.push([WORKBOARD_META_COLUMNS.TRAILING_DATES, ['=', trailingMonths]]);
  } else {
    if (fromDate) {
      filters.push([MODELS_COLUMN_IDS.VALUATION_DATE, ['>=', fromDate]]);
    }
    if (toDate) {
      filters.push([MODELS_COLUMN_IDS.VALUATION_DATE, ['<=', toDate]]);
    }
  }

  if (filterTypeSelection.assigned) {
    filters.push([WORKBOARD_META_COLUMNS.ASSIGNMENT, ['=', 'assigned']]);
  }
  if (filterTypeSelection.unassigned) {
    filters.push([WORKBOARD_META_COLUMNS.ASSIGNMENT, ['=', 'unassigned']]);
  }
  if (filterTypeSelection.all) {
    filters.push([WORKBOARD_META_COLUMNS.ASSIGNMENT, ['=', 'all']]);
  }

  const { columns, filterExclusions } = buildColumnsFromVisibility(view.columnVisibility);

  return {
    columns,
    sortKeys: [],
    groupBy: 'companies',
    filters,
    filterExclusions,
  };
};

export const tableDefinitionToModelView = (
  tableView: TableView,
  definition: WorkboardTableDefinition
): ModelView => {
  const preparerIds: ViewUserId[] = [];
  const reviewerIds: ViewUserId[] = [];
  const searchStatus: string[] = [];
  let fromDate: Date | null = null;
  let toDate: Date | null = null;

  definition.filters.forEach(([columnId, [, value]]) => {
    if (columnId === MODELS_COLUMN_IDS.MODEL_PREPARER) {
      const decoded = decodeUserRef(value);
      if (decoded) {
        preparerIds.push(decoded);
      }
      return;
    }

    if (columnId === MODELS_COLUMN_IDS.MODEL_REVIEWER) {
      const decoded = decodeUserRef(value);
      if (decoded) {
        reviewerIds.push(decoded);
      }
      return;
    }

    if (columnId === MODELS_COLUMN_IDS.MODEL_STATUS) {
      searchStatus.push(String(value));
    }

  });

  definition.filters.forEach(([columnId, [op, value]]) => {
    if (columnId !== MODELS_COLUMN_IDS.VALUATION_DATE) {
      return;
    }
    const dateValue = normalizeDate(String(value));
    if (op === '>=') {
      fromDate = dateValue;
    } else if (op === '<=') {
      toDate = dateValue;
    }
  });

  const filterTypeSelection = decodeAssignmentFilters(definition.filters);
  const filterType = filterTypeSelectionToFilterType(filterTypeSelection);
  const trailingMonths = decodeTrailingMonths(definition.filters);
  const useTrailingDates = trailingMonths != null;
  const resolvedDates = useTrailingDates
    ? getTrailingDateRange(trailingMonths ?? DEFAULT_TRAILING_MONTHS)
    : { fromDate, toDate };

  return {
    id: tableView.id != null ? String(tableView.id) : DEFAULT_VIEW_ID,
    name: tableView.name,
    preparerIds,
    reviewerIds,
    searchStatus,
    fromDate: resolvedDates.fromDate,
    toDate: resolvedDates.toDate,
    useTrailingDates,
    trailingMonths: useTrailingDates ? (trailingMonths ?? DEFAULT_TRAILING_MONTHS) : undefined,
    filterType: normalizeModelFilterType(filterType),
    filterTypeSelection: normalizeFilterTypeSelection(filterTypeSelection, filterType),
    columnVisibility: buildColumnVisibility(definition),
    visibility: tableView.visibility,
    createdBy: tableView.created_by,
  };
};

export const fromTableView = (tableView: TableView): ModelView => {
  const definition = normalizeTableDefinition(
    tableView.table_definition as WorkboardTableDefinition | undefined
  );
  return tableDefinitionToModelView(tableView, definition);
};

export const toTableViewCreatePayload = (view: ModelView): TableView => ({
  name: view.name,
  visibility: (view.visibility as TableView.visibility | undefined) ?? TableView.visibility.PRIVATE,
  table_definition: modelViewToTableDefinition(view),
  is_default: false,
});

export const toTableViewUpdatePayload = (view: ModelView): Partial<TableView> => ({
  name: view.name,
  visibility: view.visibility as TableView.visibility | undefined,
  table_definition: modelViewToTableDefinition(view),
});

export const canEditTableView = (
  view: ModelView,
  currentUserId: number | undefined,
  isStaff: boolean
): boolean => {
  if (view.id === DEFAULT_VIEW_ID) {
    return false;
  }

  if (view.visibility === TableView.visibility.PUBLIC) {
    return isStaff || view.createdBy === currentUserId;
  }

  return view.createdBy == null || view.createdBy === currentUserId;
};

export const syncModelViewsForGroupUpdate = (
  views: ModelView[],
  updated: UserGroup
): ModelView[] => {
  const syncIds = (ids: ViewUserId[]): { ids: ViewUserId[]; changed: boolean } => {
    const hasGroupEntry = ids.some(entry => entry.groupId === updated.id);
    if (!hasGroupEntry) {
      return { ids, changed: false };
    }

    const kept = ids.filter(
      entry => entry.groupId !== updated.id || updated.userIds.includes(entry.userId)
    );
    const existingUserIds = new Set(kept.map(entry => entry.userId));
    const toAdd: ViewUserId[] = updated.userIds
      .filter(userId => !existingUserIds.has(userId))
      .map(userId => ({ userId, groupId: updated.id }));

    const result = [...kept, ...toAdd];
    const changed =
      result.length !== ids.length ||
      result.some(
        (entry, index) =>
          entry.userId !== ids[index]?.userId || entry.groupId !== ids[index]?.groupId
      );

    return { ids: result, changed };
  };

  return views.map(view => {
    const prepResult = syncIds(view.preparerIds ?? []);
    const revResult = syncIds(view.reviewerIds ?? []);
    if (!prepResult.changed && !revResult.changed) {
      return view;
    }
    return {
      ...view,
      preparerIds: prepResult.ids,
      reviewerIds: revResult.ids,
    };
  });
};
